import { z } from 'zod';
/**
 * Contracts for the pre-generated coverage artifacts (see
 * `apps/api/scripts/gen-coverage-*.ts`) and the `/api/coverage/*` routes that
 * serve them. Parcel coverage is measured per county (FIPS) and rolled up to
 * a state total weighted by county population. Permit coverage is measured per
 * building-permitting jurisdiction (Census Place GEOID, county-subdivision
 * GEOID, or 5-digit county FIPS for county-issued permits) and rolled up to a
 * state total weighted by population, with every uncovered entity enumerated
 * so covered + uncovered population accounts for the whole state.
 */
const Fraction = z.number().min(0).max(1);
const NonNegInt = z.number().int().nonnegative();
const IsoDate = z.string().nullable();
// --- parcels ---------------------------------------------------------------
/** One parcel characteristic and the fraction of parcels that have it. */
export const CharacteristicCoverage = z.object({
    key: z.string(),
    label: z.string(),
    /** Fraction of parcels in the county with a non-null value, 0..1. */
    pct: Fraction,
});
export const CountyCoverage = z.object({
    state: z.string(),
    county_fips: z.string(),
    county_name: z.string(),
    population: NonNegInt,
    parcel_count: NonNegInt,
    last_refresh: IsoDate,
    characteristics: z.array(CharacteristicCoverage),
});
export const ParcelStateCoverage = z.object({
    state: z.string(),
    coverage: Fraction,
    covered_population: NonNegInt,
    total_population: NonNegInt,
    covered_counties: NonNegInt,
    total_counties: NonNegInt,
    parcel_count: NonNegInt,
});
export const ParcelCoverage = z.object({
    generated_at: z.string(),
    states: z.array(ParcelStateCoverage),
    counties: z.array(CountyCoverage),
});
/** Response of GET /api/coverage/parcels — summary only (no per-county detail). */
export const ParcelCoverageSummary = z.object({
    generated_at: z.string(),
    states: z.array(ParcelStateCoverage),
    counties: z.array(z.object({
        state: z.string(),
        county_fips: z.string(),
        county_name: z.string(),
        parcel_count: NonNegInt,
        last_refresh: IsoDate,
    })),
});
// --- permits ---------------------------------------------------------------
export const JurisdictionCoverage = z.object({
    state: z.string(),
    place_geoid: z.string(),
    place_name: z.string(),
    /**
     * Population the jurisdiction is credited with: place/county-subdivision
     * population for place-level jurisdictions, the unincorporated/county-issued
     * balance for county-level (5-digit) jurisdictions.
     */
    population: NonNegInt,
    /** Census BPS annual new-permit volume for the jurisdiction. */
    bps_volume: NonNegInt,
    permit_count: NonNegInt,
    /**
     * Trailing-12-month permit count, attributed by geography and aggregated
     * across every source feeding the jurisdiction, anchored at `last_refresh`
     * (#95). `.optional()` so snapshots written before this field shipped still
     * parse; the Permit Detail table consumes it for multi-feed jurisdictions.
     */
    permits_last_12mo: NonNegInt.optional(),
    /** Permits dated in the last 30 days (permit_date, capped at today) — the
     *  freshness the weekly load moves (#165). Optional for older snapshots. */
    permits_last_30d: NonNegInt.optional(),
    /** Permits with a lat/lon (located on the map) (#165). */
    located_count: NonNegInt.optional(),
    /** Newest permit on record (MAX permit_date, capped at today). Also the end
     *  of the continuous window (`min_continuous_date`). */
    last_refresh: IsoDate,
    /**
     * Oldest permit on record (MIN permit_date). May be a lone historical permit
     * far older than the continuous window. `.optional()` so snapshots written
     * before these fields shipped still parse.
     */
    min_permit_date: IsoDate.optional(),
    /**
     * Start of the most-recent *continuous* run of yearly permit activity: walk
     * back from the newest year while every year has ≥1 permit; a missing year
     * ends the run. Distinct from `min_permit_date` (which may predate a gap).
     * The run's end is `last_refresh`. Null when the source is dateless.
     */
    min_continuous_date: IsoDate.optional(),
    /** Distinct street addresses held — COALESCE(std_address_line1, address_line1). */
    distinct_addresses: NonNegInt.optional(),
    /** Distinct lat/lng coordinate pairs among geolocated permits. */
    distinct_coords: NonNegInt.optional(),
    /**
     * True when we hold permits for the jurisdiction but they are too
     * thin/stale/implausible to count as real coverage (fail the recency + span
     * + population-plausibility gate; molluskio#2243). Such a jurisdiction is
     * data-present-but-NOT-covered: its population is excluded from the coverage
     * numerator and it is listed among the state's uncovered entities. Absent /
     * false means it is believably covered. The UI surfaces it as a
     * "thin/partial" badge.
     */
    thin_partial: z.boolean().optional(),
});
/**
 * A permit-issuing entity we do NOT hold data for: either an independently
 * permit-issuing place, or the unincorporated/county-issued balance of a
 * county. Per state, covered_population + Σ uncovered population accounts for
 * the whole state population — nothing is silently dropped.
 */
export const UncoveredEntity = z.object({
    kind: z.enum(['place', 'county_balance']),
    name: z.string(),
    population: NonNegInt,
});
export const PermitStateCoverage = z.object({
    state: z.string(),
    coverage: Fraction,
    covered_population: NonNegInt,
    total_population: NonNegInt,
    covered_jurisdictions: NonNegInt,
    total_jurisdictions: NonNegInt,
    permit_count: NonNegInt,
    /** Σ permits_last_30d over the same jurisdictions as permit_count (#165). */
    permits_last_30d: NonNegInt.optional(),
    /** Σ located_count over the same jurisdictions as permit_count (#165). */
    located_count: NonNegInt.optional(),
    /** Every uncovered entity in the state, largest population first. */
    uncovered: z.array(UncoveredEntity),
});
export const PermitCoverage = z.object({
    generated_at: z.string(),
    states: z.array(PermitStateCoverage),
    jurisdictions: z.array(JurisdictionCoverage),
});
// --- planning / zoning ----------------------------------------------------
export const PopulationCoverageState = z.object({
    state: z.string(),
    coverage: Fraction,
    covered_population: NonNegInt,
    total_population: NonNegInt,
    covered_jurisdictions: NonNegInt,
    record_count: NonNegInt,
});
export const PopulationCoverageJurisdiction = z.object({
    state: z.string(),
    geoid: z.string(),
    name: z.string(),
    population: NonNegInt,
    record_count: NonNegInt,
    scope: z.enum(['state', 'place', 'county_balance']),
});
/** One admitted planning source × case-family coverage row. */
export const PlanningCoverageRow = z.object({
    state: z.string(),
    jurisdiction_name: z.string(),
    source_id: z.string(),
    place_geoid: z.string().nullable(),
    county_fips: z.string().nullable(),
    case_family: z.string(),
    cases_admitted: NonNegInt,
    pct_requested_action: Fraction,
    pct_located_direct: Fraction,
    pct_located_relational: Fraction,
    pct_located_any: Fraction,
    oldest_submitted: IsoDate,
    newest_submitted: IsoDate,
    days_since_newest: NonNegInt.nullable(),
    currentness: z.string(),
    quality_grade: z.string(),
    held_back_cases: NonNegInt,
    refreshed_at: z.string(),
});
export const PlanningCoverage = z.object({
    generated_at: z.string(),
    states: z.array(PopulationCoverageState),
    jurisdictions: z.array(PopulationCoverageJurisdiction),
    rows: z.array(PlanningCoverageRow),
});
// --- contractors ----------------------------------------------------------
/** One licensing authority × credential-class coverage row. */
export const ContractorCoverageRow = z.object({
    authority_id: z.string(),
    authority_name: z.string(),
    authority_scope: z.string(),
    state: z.string(),
    credential_class: z.string(),
    scope: z.string(),
    credential_rows: NonNegInt,
    distinct_entities: NonNegInt,
    distinct_people: NonNegInt,
    active_rows: NonNegInt,
    expired_rows: NonNegInt,
    inactive_rows: NonNegInt,
    unknown_status_rows: NonNegInt,
    earliest_status_observed_at: z.string().nullable(),
    latest_status_observed_at: z.string().nullable(),
    retains_history: z.boolean(),
    contributing_sources: NonNegInt,
    class_includes_individuals: z.boolean(),
    external_display_eligibility: z.string(),
    internal_use_eligibility: z.string(),
    field_policy_version: z.string(),
    computed_at: z.string(),
});
/** Link volume remains partitioned by certainty; never collapse ambiguity. */
export const ContractorLinkCoverage = z.object({
    authority_id: z.string(),
    activity_dataset: z.enum(['permits', 'planning']),
    match_tier: z.number().int().min(1).max(5),
    is_ambiguous: z.boolean(),
    links: NonNegInt,
    activities: NonNegInt,
});
export const ContractorCoverage = z.object({
    generated_at: z.string(),
    states: z.array(PopulationCoverageState),
    jurisdictions: z.array(PopulationCoverageJurisdiction),
    rows: z.array(ContractorCoverageRow),
    link_counts: z.array(ContractorLinkCoverage),
});
//# sourceMappingURL=coverage.js.map